import type { DistributionItem, EventRow } from './types';

/**
 * 분포 · 시리즈 12.
 *
 * ★★ **분류 막대는 0건인 줄도 남긴다.** 관측된 값의 목록이 아니라 **고정된
 * 분류**일 때 그렇다 — 사라지면 「0건」인지 「안 세는지」 구분할 수 없고,
 * **줄어드는 것 자체를 보려는 경우** 그 변화가 통째로 안 보인다.
 * ⚠️ 반대로 **구조적으로 불가능한 칸은 자리를 주지 않는다**(DC 21 이상 등).
 *
 * ⚠️ 라벨은 **6자 이내**다. 라벨 칸이 고정 폭이라 넘치면 말줄임표로 잘린다.
 */

/** `count`/`total` 로 한 줄 만들기. ★ 원본의 문자열 포맷을 그대로 따른다. */
function mk(label: string, count: number, total: number, unit: string): DistributionItem {
  const pct = total === 0 ? 0 : (count * 100) / total;
  return {
    label,
    count,
    pct,
    valueText: `${count}${unit}`,
    pctText: total === 0 ? '0.0%' : `${pct.toFixed(1)}%`,
    tone: '',
  };
}

/** 건수 내림차순 키. ⚠️ 동점 순서는 **삽입 순서**를 따른다(원본과 같다). */
function byCountDesc(counts: Map<string, number>): string[] {
  return [...counts.keys()].sort((a, b) => counts.get(b)! - counts.get(a)!);
}

/**
 * 턴 층 분포 — 플레이어 입력이 어디로 갔는지 **배타 분류**로.
 *
 * ★ 「막힌 것」과 「답한 것」으로 묶어 늘어놓는다(게이트 정의 v1.4 1절).
 * ⚠️ **다만 화면에는 이 순서가 안 보인다** — 도넛 범례가 값 내림차순으로 다시
 *    정렬한다. 둘을 가르는 일은 `lensBlockedSplit` 한 줄이 한다.
 * ⚠️ 「판단」과 「헛수고」는 **반려가 아니다**(회신 13 1절).
 */
export function lensBlockedDist(events?: EventRow[] | null): DistributionItem[] {
  let reached = 0; // 판정 도달 — roll · auto_success
  let aside = 0; // 판단 층 — 규칙이 재지 않는 행동 (★ 답한 것)
  let asideUnclear = 0; // 판단 층인데 콜①이 의도를 못 읽은 것 (⚠️ 막힌 것)
  let futile = 0; // 헛수고 — 판정 없이 시간만 흐른 턴
  let blocked = 0; // 대상 차단
  let impossible = 0; // 규칙상 불가능
  let unclear = 0; // 의도 불명확
  let safety = 0; // 안전 차단
  let parseFail = 0; // 해석 실패
  let lookup = 0; // 조회 (스키마 1.4.0 부터)
  let noCheck = 0; // ★ 판정이 없는 턴 — 「대화」 같은 것 (★ 답한 것)
  let other = 0; // judgment_kind 가 안 실려 온 것

  // ★★★ **판정이 없는 턴을 먼저 가려낸다.**
  //
  // ⚠️⚠️ 재현 규칙(개발 회신 46 1-1)은 **`도달 = check_resolved.kind ∈
  // {roll, bypass}`** 다. 그런데 「대화」 같은 판정 없는 동사도
  // `action_interpreted` 로 **똑같이 온다**(`parse_ok: true`). ★ 그래서
  // 그 이벤트만 보면 **판정 도달로 세어진다.**
  //
  // ★ 실측(23회차 · 2026-10-09) — `check_resolved.kind` 가 `roll` 23 ·
  // **`no_check` 5**. ⚠️ LENS 는 판정 미도달을 **18** 로 셌고 추출기는
  // **23** 이었다 — **차이가 정확히 그 5건**이었다.
  //
  // → `(runId, turnIndex)` 로 조인해 **그 턴을 「판정 없음」으로** 옮긴다.
  const noCheckTurns = new Set<string>();
  for (const e of events ?? []) {
    if (e.eventName === 'check_resolved' && e.kind === 'no_check') {
      noCheckTurns.add(`${e.runId}#${e.turnIndex}`);
    }
  }

  for (const e of events ?? []) {
    if (e.eventName === 'action_interpreted') {
      if (e.parseOk === false) parseFail++;
      else if (noCheckTurns.has(`${e.runId}#${e.turnIndex}`)) noCheck++;
      else reached++;
      continue;
    }
    if (e.eventName === 'input_blocked') {
      safety++;
      continue;
    }
    if (e.eventName !== 'unsupported_action_requested') continue;
    switch (e.judgmentKind) {
      // ★★ 재현 규칙(개발 회신 46 1-1) —
      //   `답한 것 = no_check ∪ futile ∪ (aside ∧ no_action_reason ≠ unclear)`
      //
      // ⚠️⚠️ **판단 층이 두 갈래를 삼킨다.** 2026-09-25 부터 규칙 계층이
      // `reject_unclear` · `reject_impossible` 을 `aside` 로 바꾸고(asideLayer),
      // 기원은 `aside_from` 에만 남는다(회신 46 4절). ★ 그래서 `aside` 하나로는
      // 「세계가 답한 것」과 「콜①이 의도를 못 읽은 것」이 **섞인다.**
      //
      // ★★★ 사용자 확정 **㉡ — 나눈다**(회신 46 3절). 수는 정의와 같고
      // **어느 경로로 왔는지가 화면에 남는다.** ⚠️ 합치면 되돌릴 수 없다.
      //
      // ⚠️ `=== 'unclear'` 로만 가른다 — 「없음」(옛 턴 · 1.4.0 이전)은
      // `!== 'unclear'` 라 **답한 것**으로 간다. 그것이 재현 규칙이 노린 쪽이다.
      case 'aside':
        if (e.noActionReason === 'unclear') asideUnclear++;
        else aside++;
        break;
      case 'futile': futile++; break;
      case 'blocked': blocked++; break;
      case 'reject_impossible': impossible++; break;
      case 'reject_unclear': unclear++; break;
      case 'safety_blocked': safety++; break;
      case 'parse_failed': parseFail++; break;
      // ★ 조회 — 스키마 1.4.0(2026-10-04, 개발 회신 44 4절). 정의 v1.6 이
      //   **분모와 「막힌 것」 분자에 둘 다** 넣는다.
      case 'lookup': lookup++; break;
      default: other++; break;
    }
  }

  const rows: Array<[string, number]> = [
    ['판정 도달', reached],
    // ↓ 막힌 것 — ★ 고칠 자리
    ['대상 차단', blocked],
    ['규칙상 불가능', impossible],
    ['의도 불명확', unclear],
    ['해석 실패', parseFail],
    ['안전 차단', safety],
    // ⚠️⚠️ 조회만 **0 이면 자리를 주지 않는다.** 다른 갈래와 다른 까닭 —
    //   스키마 **1.4.0 이전 회차에는 조회 이벤트가 아예 없다**(이벤트는 발행
    //   시점에 굳는다 · 개발 회신 41 9절). 그래서 0 은 「조회가 없었다」가
    //   아니라 「안 보낸다」다. ★ 고정 줄로 두면 화면이 거짓말한다.
    //   → 1.4.0 회차가 창을 채우면 고정 줄로 올린다.
    ...(lookup > 0 ? [['조회', lookup] as [string, number]] : []),
    // ⚠️ 라벨 **6자 이내**(칸이 고정 폭). 「판단」과 섞이지 않게 짓는다 —
    //   같은 `aside` 인데 **고칠 자리**라는 것이 읽혀야 한다.
    // ★ 1.4.0 이전 회차에는 `no_action_reason` 이 없어 **늘 0 이다** →
    //   조회와 같은 까닭으로 **0 이면 자리를 주지 않는다.**
    ...(asideUnclear > 0 ? [['판단·불명', asideUnclear] as [string, number]] : []),
    // ↓ 답한 것 — ★ 세계가 답했다. 정상 동작이다
    ['판단', aside],
    ['헛수고', futile],
    // ★ 「대화」 같은 판정 없는 턴. ⚠️ 1.4.0 이전에는 `no_check` 가 아예
    //   없어 늘 0 이다 → 조회와 같은 까닭으로 **0 이면 자리를 주지 않는다.**
    ...(noCheck > 0 ? [['판정 없음', noCheck] as [string, number]] : []),
  ];
  // ⚠️ 유니온 밖의 값만 조건부다 — 안 와야 정상이라 0 이면 자리를 주지 않는다.
  if (other > 0) rows.push(['사유 미분류', other]);

  const total = rows.reduce((a, r) => a + r[1], 0);
  return rows.map(([label, n]) => mk(label, n, total, '건'));
}

/**
 * 판정 미도달을 **막힌 것 / 답한 것**으로 갈라 한 줄로.
 *
 * ★★★ 둘은 성격이 다르다 — 막힌 것은 **고칠 자리**, 답한 것은 **세계가 답한
 * 것, 정상 동작**이다. 층 여덟 줄만 평평하게 보여주면 어느 줄을 고쳐야 하는지
 * 화면이 말하지 않는다.
 *
 * ⚠️⚠️ **빼지 않고 나눈다.** 분자에서 빼면 「판정 미도달」이라는 이름이
 * 거짓말이 되고(플레이어 눈에는 「쳤는데 주사위가 안 돌았다」로 같다).
 */
export function lensBlockedSplit(items?: DistributionItem[] | null): string {
  // ★ 조회는 정의 v1.6 이 「막힌 것」에 둔다(「★ 막힌 것  unclear · 반려 ·
  //   설계된 차단 · 안전 차단 · **조회** · 분류 불가」).
  const stuck = ['대상 차단', '규칙상 불가능', '의도 불명확', '해석 실패', '안전 차단', '조회', '판단·불명'];
  const answered = ['판단', '헛수고', '판정 없음'];

  let total = 0;
  let reached = 0;
  let st = 0;
  let an = 0;
  let other = 0;
  for (const it of items ?? []) {
    const n = it.count ?? 0;
    total += n;
    if (it.label === '판정 도달') reached += n;
    else if (stuck.includes(it.label)) st += n;
    else if (answered.includes(it.label)) an += n;
    // 유니온 밖(「사유 미분류」 등)은 **막힌 것**이다 — 정의 v1.4 가 분류 불가를
    // 그쪽에 둔다.
    else other += n;
  }
  // ⚠️ 표본이 없으면 **빈 문자열**이다 — 화면이 줄을 숨긴다. 0% 로 적으면
  //    「막힌 것이 없다」로 읽힌다.
  if (total === 0) return '';

  const unreached = total - reached;
  const stuckAll = st + other;
  const pct = (n: number) => ((n * 100) / total).toFixed(1);

  return (
    `판정 미도달 ${unreached}건 (${pct(unreached)}%) — ` +
    `막힌 것 ${stuckAll}건 (${pct(stuckAll)}%) · ` +
    `답한 것 ${an}건 (${pct(an)}%). ` +
    `막힌 것이 고칠 자리입니다. 답한 것은 세계가 답한 턴이라 정상입니다.`
  );
}

/** 진행 불가 유형을 도넛용 문자열로. ★ 0건 갈래를 되살린다. */
export function lensBlockedDonutSeries(items?: DistributionItem[] | null): string {
  const fixedLabels = [
    '판정 도달', '판단', '헛수고', '대상 차단',
    '규칙상 불가능', '의도 불명확', '해석 실패', '안전 차단',
  ];
  const counts = new Map<string, number>();
  for (const it of items ?? []) counts.set(it.label, it.count ?? 0);

  const rows: Array<[string, number]> = [
    ...fixedLabels.map((k) => [k, counts.get(k) ?? 0] as [string, number]),
    // 유니온 밖의 라벨은 값이 있을 때만 뒤에 붙인다.
    ...[...counts.keys()]
      .filter((k) => !fixedLabels.includes(k) && counts.get(k)! > 0)
      .map((k) => [k, counts.get(k)!] as [string, number]),
  ];
  // 큰 조각부터 — 도넛은 12시부터 시계방향, 색이 진한 쪽이 앞이다.
  rows.sort((a, b) => b[1] - a[1]);
  return rows.map(([k, v]) => `${k}|${v}`).join(';');
}

/**
 * 이탈 사유 분포. ⚠️ **단위가 이벤트가 아니라 회차다** — `scene_exited` 는
 * 장면마다 나와서 그대로 세면 「정상 완료」가 압도한다.
 *
 * ★ `app_closed`/`timeout`/`abandoned` 는 **발행되지 않는다**(세션 하트비트가
 * 없어 판별 수단이 아예 없다). 그래서 이탈은 「`run_started` 는 있는데
 * `ending_reached` 가 없는 회차」로 읽고, 사유가 기록된 것을 뺀 나머지를
 * **「사유 미기록」**으로 넣는다. ⚠️ **이 줄이 없으면 이탈이 통째로 사라져
 * 아무도 안 나간 것처럼 보인다.**
 */
export function lensExitReasonDist(events?: EventRow[] | null): DistributionItem[] {
  const labels: Record<string, string> = {
    broken_transition: '끊긴 전이',
    captured: '붙잡힘',
    app_closed: '앱 종료',
    timeout: '타임아웃',
    error: '오류',
    abandoned: '이탈',
  };
  const started = new Set<string>();
  const ended = new Set<string>();
  const reasonOf = new Map<string, string>();
  for (const e of events ?? []) {
    if (e.runId === '') continue;
    if (e.eventName === 'run_started') started.add(e.runId);
    if (e.eventName === 'ending_reached') ended.add(e.runId);
    if (
      e.eventName === 'scene_exited' &&
      e.exitReason !== '' &&
      e.exitReason !== 'completed'
    ) {
      // 한 회차에 여러 번 나올 수 있으면 마지막 것이 그 회차의 끝이다.
      reasonOf.set(e.runId, e.exitReason);
    }
  }
  const counts = new Map<string, number>();
  for (const [runId, reason] of reasonOf) {
    if (ended.has(runId)) continue;
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  const quit = started.size - ended.size;
  const explained = [...counts.values()].reduce((a, b) => a + b, 0);
  if (quit - explained > 0) counts.set('__none', quit - explained);
  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  return byCountDesc(counts).map((k) =>
    mk(k === '__none' ? '사유 미기록' : (labels[k] ?? k), counts.get(k)!, total, '회차'),
  );
}

/**
 * 결말 분포. ★ **성패로 분류하지 않는다** — 갈래(`ending_kind`)는 라벨에
 * 덧붙이기만 한다. **등급이 아니라 종류다**(확정본 2-③: 스키마가 작가가
 * 거부한 위계를 만들어내지 않도록).
 */
export function lensEndingDist(events?: EventRow[] | null): DistributionItem[] {
  const kindLabels: Record<string, string> = {
    narrative: '이야기',
    capture: '붙잡힘',
    abandoned: '중단',
  };
  const counts = new Map<string, number>();
  const kinds = new Map<string, string>();
  for (const e of events ?? []) {
    if (e.eventName !== 'ending_reached') continue;
    const id = e.endingId === '' ? '(미상)' : e.endingId;
    counts.set(id, (counts.get(id) ?? 0) + 1);
    if (e.endingKind !== '') kinds.set(id, e.endingKind);
  }
  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  return byCountDesc(counts).map((k) => {
    const kind = kinds.get(k);
    const label = kind === undefined ? k : `${k} · ${kindLabels[kind] ?? kind}`;
    return mk(label, counts.get(k)!, total, '회');
  });
}

/**
 * 판정 결과 — 굴림 성공 · 굴림 실패 · 우회.
 *
 * ⚠️⚠️ **모르는 `kind` 를 「굴림 실패」로 세지 않는다.** 「대화」(판정 없는 동사)
 * 처럼 새 `kind` 가 오면 `success` 가 안 실리고, 게터가 `false` 로 접어
 * **판정이 아예 없었던 턴이 「굴림 실패」로 둔갑**한다(회신 32 1-3).
 * ★ 빈 값은 **옛 발행**이라 굴림으로 본다 — 새 값이 생긴 것과 필드가 없는 것은
 *   다르다.
 */
export function lensCheckOutcomeDist(events?: EventRow[] | null): DistributionItem[] {
  let rollOk = 0;
  let rollFail = 0;
  let bypass = 0;
  let noCheck = 0;
  for (const e of events ?? []) {
    if (e.eventName !== 'check_resolved') continue;
    if (e.kind !== '' && e.kind !== 'roll' && e.kind !== 'bypass') {
      noCheck++;
      continue;
    }
    if (e.kind === 'bypass') bypass++;
    else if (e.success === true) rollOk++;
    else rollFail++;
  }
  // ★ **0건인 줄도 남긴다** — 셋은 고정된 분류다.
  const rows: Array<[string, number]> = [
    ['굴림 성공', rollOk],
    ['굴림 실패', rollFail],
    ['우회 통과', bypass],
  ];
  // ★ `no_check` 가 확정 이름이다(회신 36 1-2). 0건이면 줄을 만들지 않는다 —
  //   **값이 오면 그 자체가 신호**다.
  if (noCheck > 0) rows.push(['판정 없음', noCheck]);
  // ⚠️ 분모는 셋의 합이다 — 「판정 없음」은 비율 분모에 안 들어간다(원본과 같다).
  const total = rollOk + rollFail + bypass;
  return rows.map(([label, n]) => mk(label, n, total, '건'));
}

/**
 * DC 구간 분포.
 *
 * ★ 구간은 **설명일 뿐 합격선이 아니다** — `roll_result` 가 능력치·숙련 보정을
 * 더한 최종 판정치라 도달 상한이 동사마다 다르다(이동 1~20, 조사 4~23).
 * ⚠️ **런타임 DC 는 5~20 에만 존재한다** — 「21 이상」은 영영 비어 있어 자리를
 *    주지 않는다. ★ 16-20 을 둘로 쪼갠 이유: 이동 DC 상한 수정(20→17)이 한
 *    칸이면 칸 안에서 일어나 **화면에 안 보인다.**
 */
export function lensCheckDcDist(events?: EventRow[] | null): DistributionItem[] {
  const buckets: Array<[number, number]> = [
    [0, 10], [11, 13], [14, 15], [16, 17], [18, 20],
  ];
  const labels = ['DC 5-10', 'DC 11-13', 'DC 14-15', 'DC 16-17', 'DC 18-20'];
  const counts = new Array<number>(buckets.length).fill(0);
  for (const e of events ?? []) {
    if (e.eventName !== 'check_resolved') continue;
    // ⚠️ **굴림만 본다.** 모르는 kind 를 넣으면 최저 DC 구간으로 찍힌다 —
    //    판정이 없었던 것이지 못 넘은 것이 아니다.
    if (e.kind !== '' && e.kind !== 'roll') continue;
    // 원본에는 여기 `if (e.kind == 'bypass') continue;` 가 한 줄 더 있다.
    // TS 가 도달 불가를 증명했다(TS2367) — 바로 위 줄이 'roll' 과 빈 값
    // 외를 전부 걸러내므로 bypass 는 여기 닿지 않는다. 값은 같다.
    // ⚠️ 원본에는 여기 `if (e.kind == 'bypass') continue;` 가 한 줄 더 있다.
    //    ★ **TS 가 도달 불가를 증명했다**(TS2367) — 바로 위 줄이 `roll` 과
    //    빈 값 외를 전부 걸러내므로 `bypass` 는 여기 닿지 않는다.
    //    값은 같고(정답지가 확인한다) 죽은 줄만 사라졌다.
    const dc = e.dc;
    for (let i = 0; i < buckets.length; i++) {
      if (dc >= buckets[i]![0] && dc <= buckets[i]![1]) {
        counts[i]!++;
        break;
      }
    }
  }
  const total = counts.reduce((a, b) => a + b, 0);
  // ★ **0건 구간도 남긴다** — 전부 도달 가능한 난도라 0 이 정보다.
  return labels.map((label, i) => mk(label, counts[i]!, total, '건'));
}

/**
 * 동사별 시도 분포.
 *
 * ★ **거부된 시도도 센다.** 규칙 계층이 거부한 턴은 `action_interpreted` 를
 * 발행하지 않고 `unsupported_action_requested` 에 `action_type` 을 싣는다 —
 * 앞의 것만 세면 **콘텐츠 보강 신호가 통째로 빠진다.**
 */
export function lensVerbDist(events?: EventRow[] | null): DistributionItem[] {
  const counts = new Map<string, number>();
  for (const e of events ?? []) {
    if (
      e.eventName !== 'action_interpreted' &&
      e.eventName !== 'unsupported_action_requested'
    ) continue;
    if (e.actionType === '') continue;
    counts.set(e.actionType, (counts.get(e.actionType) ?? 0) + 1);
  }
  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  return byCountDesc(counts).map((k) => mk(k, counts.get(k)!, total, '회'));
}

/**
 * 한 장면의 동사 분포. ★ `lensVerbDist` 와 **같은 규칙**이어야 전체와 장면의
 * 합이 맞는다.
 */
export function lensSceneVerbDist(
  events?: EventRow[] | null,
  sceneId?: string | null,
): DistributionItem[] {
  const target = sceneId ?? '';
  const counts = new Map<string, number>();
  for (const e of events ?? []) {
    if (e.sceneId !== target) continue;
    if (
      e.eventName !== 'action_interpreted' &&
      e.eventName !== 'unsupported_action_requested'
    ) continue;
    if (e.actionType === '') continue;
    counts.set(e.actionType, (counts.get(e.actionType) ?? 0) + 1);
  }
  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  return byCountDesc(counts).map((k) => mk(k, counts.get(k)!, total, '회'));
}

/**
 * 동사별 판정 성공률.
 *
 * ★ **동사는 `check_resolved` 에 직접 실려 온다.** 없을 때만 같은 턴의
 * `action_interpreted` 와 `(run_id, turn_index)` 로 조인한다 — 조인을 지우지
 * 않는 이유가 둘이다: 발행 전 데이터에도 돌아야 하고, **두 경로가 서로
 * 검산**이 된다.
 *
 * ★★ **`pct` 는 점유율이 아니라 성공률이다.** 막대 길이가 곧 성공률이라
 * 동사 사이 난도 차이가 한눈에 보인다. ⚠️ 그래서 **성공률 오름차순으로
 * 정렬**한다 — 건수로 정렬하고 비율을 그리면 막대가 오르내려 고장 난 것처럼
 * 보인다.
 */
export function lensCheckVerbStats(events?: EventRow[] | null): DistributionItem[] {
  const verbOf = new Map<string, string>();
  for (const e of events ?? []) {
    if (e.eventName !== 'action_interpreted') continue;
    if (e.actionType === '') continue;
    verbOf.set(`${e.runId}#${e.turnIndex}`, e.actionType);
  }

  const tries = new Map<string, number>();
  const wins = new Map<string, number>();
  for (const e of events ?? []) {
    if (e.eventName !== 'check_resolved') continue;
    if (e.kind !== '' && e.kind !== 'roll') continue;
    // 원본에는 여기 `if (e.kind == 'bypass') continue;` 가 한 줄 더 있다.
    // TS 가 도달 불가를 증명했다(TS2367) — 바로 위 줄이 'roll' 과 빈 값
    // 외를 전부 걸러내므로 bypass 는 여기 닿지 않는다. 값은 같다.
    const verb = e.actionType !== '' ? e.actionType : verbOf.get(`${e.runId}#${e.turnIndex}`);
    if (verb === undefined) continue;
    tries.set(verb, (tries.get(verb) ?? 0) + 1);
    if (e.success === true) wins.set(verb, (wins.get(verb) ?? 0) + 1);
  }

  const keys = [...tries.keys()].sort((a, b) => {
    const ra = (wins.get(a) ?? 0) / tries.get(a)!;
    const rb = (wins.get(b) ?? 0) / tries.get(b)!;
    return ra === rb ? 0 : ra < rb ? -1 : 1;
  });
  return keys.map((k) => {
    const n = tries.get(k)!;
    const w = wins.get(k) ?? 0;
    const rate = (w * 100) / n;
    return { label: k, count: n, pct: rate, valueText: `${w}/${n}`, pctText: `${rate.toFixed(1)}%`, tone: '' };
  });
}

/**
 * 응답 시간 구간 분포.
 *
 * ★ **구간 경계를 기준선(5초·12초)에 맞췄다.** 경계가 기준선과 어긋나면 막대를
 * 보고도 몇 퍼센트가 기준을 넘었는지 셀 수 없다.
 * ⚠️ **마지막 구간은 0이어도 남긴다** — 꼬리를 보라고 만든 패널인데 그 칸이
 *    사라지면 「0건」인지 「측정 안 됨」인지 구분할 수 없다.
 */
export function lensLatencyDist(events?: EventRow[] | null): DistributionItem[] {
  const edges = [3000, 5000, 12000];
  const labels = ['3초 미만', '3-5초', '5-12초', '12초 초과'];
  const counts = new Array<number>(labels.length).fill(0);
  for (const e of events ?? []) {
    const ms = e.durationMs;
    if (ms <= 0) continue;
    let i = edges.length;
    for (let k = 0; k < edges.length; k++) {
      if (ms < edges[k]!) { i = k; break; }
    }
    counts[i]!++;
  }
  const total = counts.reduce((a, b) => a + b, 0);
  const last = labels.length - 1;
  const out: DistributionItem[] = [];
  for (let i = 0; i < labels.length; i++) {
    if (counts[i]! > 0 || i === last) out.push(mk(labels[i]!, counts[i]!, total, '건'));
  }
  return out;
}

/** AI 오서술을 검증 실패 사유별로. */
export function lensNarrationDist(events?: EventRow[] | null): DistributionItem[] {
  const counts = new Map<string, number>();
  for (const e of events ?? []) {
    if (e.eventName !== 'narration_validation_failed') continue;
    const reason = e.reason === '' ? '기타' : e.reason;
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  return byCountDesc(counts).map((k) => mk(k, counts.get(k)!, total, '건'));
}
